import { useState, useEffect } from "react";
import { apiClient, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  ReferenceLine
} from "recharts";
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Wrench,
  Fuel,
  ClipboardList,
  Calendar,
  Plus,
  Edit,
  RefreshCw,
  Target,
  PieChart
} from "lucide-react";
import { toast } from "sonner";

const formatCurrency = (value) => {
  if (!value && value !== 0) return "TZS 0";
  return new Intl.NumberFormat("en-TZ", {
    style: "currency",
    currency: "TZS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatShortCurrency = (value) => {
  if (!value && value !== 0) return "0";
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return value.toFixed(0);
};

const statusConfig = {
  on_track: { label: "On Track", color: "text-green-400", bgColor: "bg-green-500/20", icon: CheckCircle },
  trending_high: { label: "Trending High", color: "text-yellow-400", bgColor: "bg-yellow-500/20", icon: TrendingUp },
  at_risk: { label: "At Risk", color: "text-orange-400", bgColor: "bg-orange-500/20", icon: AlertTriangle },
  over_budget: { label: "Over Budget", color: "text-red-400", bgColor: "bg-red-500/20", icon: TrendingDown },
  no_budget: { label: "No Budget Set", color: "text-muted-foreground", bgColor: "bg-muted/20", icon: Target }
};

const categoryConfig = {
  maintenance: { label: "Maintenance", icon: Wrench, color: "text-blue-400" },
  fuel: { label: "Fuel", icon: Fuel, color: "text-green-400" },
  repairs: { label: "Repairs", icon: ClipboardList, color: "text-yellow-400" },
  total: { label: "Total", icon: DollarSign, color: "text-primary" }
};

export default function BudgetPage() {
  const { user } = useAuth();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [formData, setFormData] = useState({
    maintenance_budget: "",
    fuel_budget: "",
    repairs_budget: "",
    notes: ""
  });

  useEffect(() => {
    fetchBudgetTracking();
  }, [selectedYear]);

  const fetchBudgetTracking = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/budgets/tracking/${selectedYear}`);
      setTracking(res.data);
    } catch (error) {
      console.error("Failed to fetch budget tracking:", error);
      toast.error("Failed to fetch budget data");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (budget = null) => {
    if (budget) {
      setEditingBudget(budget);
      setFormData({
        maintenance_budget: tracking?.budget?.maintenance_budget?.toString() || "",
        fuel_budget: tracking?.budget?.fuel_budget?.toString() || "",
        repairs_budget: tracking?.budget?.repairs_budget?.toString() || "",
        notes: ""
      });
    } else {
      setEditingBudget(null);
      setFormData({
        maintenance_budget: "",
        fuel_budget: "",
        repairs_budget: "",
        notes: ""
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        maintenance_budget: parseFloat(formData.maintenance_budget) || 0,
        fuel_budget: parseFloat(formData.fuel_budget) || 0,
        repairs_budget: parseFloat(formData.repairs_budget) || 0,
        notes: formData.notes || null
      };

      if (tracking?.budget_id) {
        await apiClient.put(`/budgets/${tracking.budget_id}`, data);
        toast.success("Budget updated successfully");
      } else {
        await apiClient.post("/budgets", {
          ...data,
          organization_id: user.organization_id,
          year: selectedYear
        });
        toast.success("Budget created successfully");
      }
      setDialogOpen(false);
      fetchBudgetTracking();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save budget");
    }
  };

  const BudgetCard = ({ category, data }) => {
    const config = categoryConfig[category];
    const status = statusConfig[data.status] || statusConfig.no_budget;
    const Icon = config.icon;
    const StatusIcon = status.icon;

    return (
      <Card className="bg-card border-border rounded-sm" data-testid={`budget-card-${category}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-sm ${status.bgColor} flex items-center justify-center`}>
                <Icon className={`w-4 h-4 ${config.color}`} />
              </div>
              <span className="font-heading text-sm tracking-wide text-foreground">{config.label}</span>
            </div>
            <Badge className={`${status.bgColor} ${status.color} border-0 font-mono text-[10px]`}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {status.label}
            </Badge>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-xs font-mono">
              <span className="text-muted-foreground">Spent</span>
              <span className="text-foreground">{formatCurrency(data.spent)}</span>
            </div>
            <div className="flex justify-between text-xs font-mono">
              <span className="text-muted-foreground">Budget</span>
              <span className="text-foreground">{formatCurrency(data.budget)}</span>
            </div>
            <Progress 
              value={Math.min(100, data.percentUsed)} 
              className={`h-2 ${data.percentUsed > 100 ? "[&>div]:bg-red-500" : data.percentUsed > 90 ? "[&>div]:bg-yellow-500" : ""}`}
            />
            <div className="flex justify-between text-[10px] font-mono">
              <span className="text-muted-foreground">{data.percentUsed.toFixed(1)}% used</span>
              <span className={data.percentForecast > 100 ? "text-red-400" : "text-muted-foreground"}>
                Forecast: {data.percentForecast.toFixed(1)}%
              </span>
            </div>
          </div>
          
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex justify-between text-xs font-mono">
              <span className="text-muted-foreground">Year-End Forecast</span>
              <span className={data.forecast > data.budget ? "text-red-400 font-bold" : "text-green-400"}>
                {formatCurrency(data.forecast)}
              </span>
            </div>
            {data.forecast > data.budget && data.budget > 0 && (
              <p className="text-[10px] font-mono text-red-400 mt-1">
                ⚠ Projected to exceed by {formatCurrency(data.forecast - data.budget)}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="budget-loading">
        <div className="skeleton-tactical h-8 w-48 rounded" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="skeleton-tactical h-48 rounded" />
          ))}
        </div>
      </div>
    );
  }

  // Prepare chart data
  const chartData = tracking?.monthly_breakdown?.map(m => ({
    name: m.month_name,
    Maintenance: m.maintenance,
    Fuel: m.fuel,
    Repairs: m.repairs,
    Total: m.total
  })) || [];

  // Calculate cumulative spending for forecast line
  let cumulative = 0;
  const cumulativeData = tracking?.monthly_breakdown?.map(m => {
    cumulative += m.total;
    return {
      name: m.month_name,
      Cumulative: cumulative,
      Budget: tracking?.budget?.total_budget ? (tracking.budget.total_budget / 12) * m.month : 0
    };
  }) || [];

  return (
    <div className="space-y-6" data-testid="budget-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight text-foreground">
            BUDGET TRACKING
          </h1>
          <p className="text-sm font-mono text-muted-foreground mt-1">
            Monitor spending against allocated budget and forecasts
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-32 bg-input border-border rounded-sm font-mono" data-testid="year-selector">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border rounded-sm">
              {[currentYear - 1, currentYear, currentYear + 1].map(year => (
                <SelectItem key={year} value={year.toString()} className="font-mono">{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {user?.role === "admin" && (
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-heading tracking-wider"
              data-testid="set-budget-btn"
            >
              {tracking?.has_budget ? <Edit className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {tracking?.has_budget ? "EDIT BUDGET" : "SET BUDGET"}
            </Button>
          )}
        </div>
      </div>

      {/* Year Progress */}
      <Card className="bg-card border-border rounded-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-mono text-muted-foreground">YEAR PROGRESS</span>
            <span className="text-xs font-mono text-foreground">{tracking?.year_progress}% ({tracking?.months_elapsed} months)</span>
          </div>
          <Progress value={tracking?.year_progress || 0} className="h-2" />
        </CardContent>
      </Card>

      {/* Budget Status Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <BudgetCard 
          category="total"
          data={{
            spent: tracking?.actual?.total || 0,
            budget: tracking?.budget?.total_budget || 0,
            forecast: tracking?.forecast?.total || 0,
            percentUsed: tracking?.percentage_used?.total || 0,
            percentForecast: tracking?.forecast_percentage?.total || 0,
            status: tracking?.status?.total || "no_budget"
          }}
        />
        <BudgetCard 
          category="maintenance"
          data={{
            spent: tracking?.actual?.maintenance || 0,
            budget: tracking?.budget?.maintenance_budget || 0,
            forecast: tracking?.forecast?.maintenance || 0,
            percentUsed: tracking?.percentage_used?.maintenance || 0,
            percentForecast: tracking?.forecast_percentage?.maintenance || 0,
            status: tracking?.status?.maintenance || "no_budget"
          }}
        />
        <BudgetCard 
          category="fuel"
          data={{
            spent: tracking?.actual?.fuel || 0,
            budget: tracking?.budget?.fuel_budget || 0,
            forecast: tracking?.forecast?.fuel || 0,
            percentUsed: tracking?.percentage_used?.fuel || 0,
            percentForecast: tracking?.forecast_percentage?.fuel || 0,
            status: tracking?.status?.fuel || "no_budget"
          }}
        />
        <BudgetCard 
          category="repairs"
          data={{
            spent: tracking?.actual?.repairs || 0,
            budget: tracking?.budget?.repairs_budget || 0,
            forecast: tracking?.forecast?.repairs || 0,
            percentUsed: tracking?.percentage_used?.repairs || 0,
            percentForecast: tracking?.forecast_percentage?.repairs || 0,
            status: tracking?.status?.repairs || "no_budget"
          }}
        />
      </div>

      {/* Pending Repairs Warning */}
      {tracking?.pending?.count > 0 && (
        <Card className="bg-yellow-500/10 border-yellow-500/30 rounded-sm" data-testid="pending-repairs-warning">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="w-5 h-5 text-yellow-400" />
            <div>
              <p className="text-sm font-heading text-yellow-400">PENDING REPAIRS</p>
              <p className="text-xs font-mono text-yellow-400/70">
                {tracking.pending.count} pending repair order(s) with estimated cost of {formatCurrency(tracking.pending.repairs)} - included in forecast
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Over Budget Alert */}
      {tracking?.status?.total === "over_budget" && (
        <Card className="bg-red-500/10 border-red-500/30 rounded-sm" data-testid="over-budget-alert">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-400 animate-pulse" />
            <div>
              <p className="text-sm font-heading text-red-400">BUDGET EXCEEDED FORECAST</p>
              <p className="text-xs font-mono text-red-400/70">
                Based on current spending rate, you are projected to exceed the annual budget by {formatCurrency((tracking?.forecast?.total || 0) - (tracking?.budget?.total_budget || 0))}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Spending */}
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="border-b border-border pb-3">
            <CardTitle className="font-heading text-sm tracking-wide flex items-center gap-2">
              <PieChart className="w-4 h-4 text-primary" />
              MONTHLY SPENDING BREAKDOWN
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
                  <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} tickFormatter={(v) => formatShortCurrency(v)} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '4px' }}
                    formatter={(value) => formatCurrency(value)}
                  />
                  <Legend />
                  <Bar dataKey="Maintenance" fill="hsl(var(--chart-1))" stackId="a" />
                  <Bar dataKey="Fuel" fill="hsl(var(--chart-2))" stackId="a" />
                  <Bar dataKey="Repairs" fill="hsl(var(--chart-3))" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Cumulative vs Budget */}
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="border-b border-border pb-3">
            <CardTitle className="font-heading text-sm tracking-wide flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              CUMULATIVE SPENDING VS BUDGET
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={cumulativeData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
                  <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} tickFormatter={(v) => formatShortCurrency(v)} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '4px' }}
                    formatter={(value) => formatCurrency(value)}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="Cumulative" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                  {tracking?.budget?.total_budget > 0 && (
                    <ReferenceLine y={tracking.budget.total_budget} stroke="hsl(var(--destructive))" strokeDasharray="5 5" label={{ value: 'Budget', fill: 'hsl(var(--destructive))', fontSize: 10 }} />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Average Monthly Spending */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader className="border-b border-border pb-3">
          <CardTitle className="font-heading text-sm tracking-wide">AVERAGE MONTHLY SPENDING</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-xl font-heading font-bold text-primary">{formatShortCurrency(tracking?.average_monthly?.total || 0)}</p>
              <p className="text-[10px] font-mono text-muted-foreground">TOTAL/MONTH</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-heading font-bold text-blue-400">{formatShortCurrency(tracking?.average_monthly?.maintenance || 0)}</p>
              <p className="text-[10px] font-mono text-muted-foreground">MAINTENANCE/MONTH</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-heading font-bold text-green-400">{formatShortCurrency(tracking?.average_monthly?.fuel || 0)}</p>
              <p className="text-[10px] font-mono text-muted-foreground">FUEL/MONTH</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-heading font-bold text-yellow-400">{formatShortCurrency(tracking?.average_monthly?.repairs || 0)}</p>
              <p className="text-[10px] font-mono text-muted-foreground">REPAIRS/MONTH</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Budget Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader className="border-b border-border pb-3">
            <DialogTitle className="font-heading text-lg tracking-wide flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              {tracking?.has_budget ? "EDIT" : "SET"} BUDGET FOR {selectedYear}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="text-xs font-mono text-muted-foreground">MAINTENANCE BUDGET (TZS)</Label>
              <Input
                type="number"
                value={formData.maintenance_budget}
                onChange={(e) => setFormData({...formData, maintenance_budget: e.target.value})}
                placeholder="e.g., 50000000"
                className="bg-input border-border rounded-sm font-mono"
                data-testid="maintenance-budget-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono text-muted-foreground">FUEL BUDGET (TZS)</Label>
              <Input
                type="number"
                value={formData.fuel_budget}
                onChange={(e) => setFormData({...formData, fuel_budget: e.target.value})}
                placeholder="e.g., 100000000"
                className="bg-input border-border rounded-sm font-mono"
                data-testid="fuel-budget-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono text-muted-foreground">REPAIRS BUDGET (TZS)</Label>
              <Input
                type="number"
                value={formData.repairs_budget}
                onChange={(e) => setFormData({...formData, repairs_budget: e.target.value})}
                placeholder="e.g., 30000000"
                className="bg-input border-border rounded-sm font-mono"
                data-testid="repairs-budget-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono text-muted-foreground">NOTES (OPTIONAL)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any notes about this budget..."
                className="bg-input border-border rounded-sm font-mono resize-none"
                rows={2}
              />
            </div>
            <div className="bg-accent/30 p-3 rounded-sm">
              <div className="flex justify-between text-sm font-mono">
                <span className="text-muted-foreground">Total Budget:</span>
                <span className="text-primary font-bold">
                  {formatCurrency(
                    (parseFloat(formData.maintenance_budget) || 0) +
                    (parseFloat(formData.fuel_budget) || 0) +
                    (parseFloat(formData.repairs_budget) || 0)
                  )}
                </span>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" className="bg-primary text-primary-foreground rounded-sm font-heading tracking-wider" data-testid="save-budget-btn">
                SAVE BUDGET
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
